import React, { useState } from "react";
import { api, type Me } from "./api";
import { useProblems, FieldProblem, invalid } from "./form-check";
import { stamp } from "./Pass";
import { appNow } from "./clock";
import { easternToUtcMs } from "../server/station/windows";
import { hhmmOf } from "./Visits";

/**
 * Report #17, the parent-notification queue (build plan §10.1 #4; D1 D5; D2
 * §F): To release (email drafts; an RN or Supervisor releases and sends in
 * one click, editing first if needed) · Phone: not yet reached (log each
 * call; a reached parent needs a summary, and drafts the follow-up email) ·
 * Sent · All. Overdue drafts are amber. In the test app "sent" means the Test
 * Outbox.
 */

export interface QueueLine {
  notificationKey: string;
  personKey: string;
  lastName: string;
  firstName: string;
  city: string;
  visitKey: string;
  tier: string;
  method: string;
  status: string;
  adultSelf: boolean;
  toName: string;
  toContact: string;
  createdUtc: string;
  dueByUtc: string;
  overdue: boolean;
  attempts: { atUtc: string; number: string; outcome: string; byName: string; summary?: string }[];
  reachedAtUtc: string;
  sentAtUtc: string;
  approvedByEmail: string;
  summary: string;
  templateKey: string;
  templateLabel: string;
  templateBadge: string;
  edited: boolean;
  subject: string;
  body: string;
  left: string[];
  tabs: ("release" | "phone" | "sent" | "insurance")[];
  /** M5: what the row is about. */
  kind: "visit" | "eow" | "packet" | "insurance";
}

const TABS = [
  ["release", "To release"],
  ["phone", "Phone: not yet reached"],
  ["sent", "Sent"],
  // M5: precautionary notices of claim, sent only when a System Admin approves.
  ["insurance", "Insurance notices"],
  ["all", "All"],
] as const;
const OUTCOME_LABEL: Record<string, string> = { Reached: "Reached", NoAnswer: "No answer", LeftMessage: "Left a message" };
const STATUS_LABEL: Record<string, string> = { Draft: "Draft", Approved: "Released, not sent", Sent: "Sent", Attempting: "Calling", Reached: "Reached", Withdrawn: "Withdrawn", Failed: "Not reached" };
const nowHHMM = () => hhmmOf(new Date(appNow()).toISOString());

function EmailActions({ l, me, onLine }: { l: QueueLine; me: Me; onLine: (l: QueueLine | null) => void }) {
  const insurance = l.tabs.includes("insurance");
  // A precautionary notice is the System Admin's to edit and approve (approve = send); every other email, an RN's or Supervisor's.
  const canRelease = insurance ? me.capabilities.includes("insurance.approve") : me.capabilities.includes("notification.release");
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(l.body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function call(method: string, path: string, data: unknown) {
    setBusy(true);
    setError(null);
    const r = await api<{ line: QueueLine | null }>(method, path, data);
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "That didn't work."), false;
    onLine(r.data.line);
    return true;
  }
  const k = encodeURIComponent(l.notificationKey);
  if (!l.tabs.includes("release") && !insurance) return null;
  return (
    <div className="mt-2 space-y-2">
      {editing ? (
        <div>
          <label htmlFor={`nq-body-${l.notificationKey}`} className="field-label">The message as it will be sent</label>
          <textarea id={`nq-body-${l.notificationKey}`} className="field-input min-h-48 font-mono text-sm" value={body} onChange={(e) => setBody(e.target.value)} />
          <div className="mt-1 flex flex-wrap gap-2">
            <button type="button" className="btn-secondary min-h-11" disabled={busy} onClick={async () => (await call("PUT", `/api/notifications/${k}`, { body })) && setEditing(false)}>Save the edit</button>
            <button type="button" className="btn-secondary min-h-11" onClick={() => { setEditing(false); setBody(l.body); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <pre className="whitespace-pre-wrap break-words rounded border border-slate-200 bg-slate-50 p-2 text-sm">{l.body}</pre>
      )}
      {l.left.length > 0 && <p className="text-sm font-medium text-amber-900">Complete before sending: {l.left.join(", ")}</p>}
      <div className="flex flex-wrap gap-2">
        {canRelease && <button type="button" className="btn-primary min-h-11" disabled={busy || editing} onClick={() => void call("POST", `/api/notifications/${k}/${insurance ? "approve-insurance" : "release"}`, {})}>{insurance ? "Approve and send" : "Release and send"}</button>}
        {canRelease && !editing && <button type="button" className="btn-secondary min-h-11" onClick={() => setEditing(true)}>Edit the message</button>}
        {me.licensed && l.tier !== "Phone" && l.kind === "visit" && <button type="button" className="btn-secondary min-h-11" disabled={busy} onClick={() => void call("POST", `/api/notifications/${k}/raise`, {})}>Raise to a phone call</button>}
      </div>
      {!canRelease && <p className="text-xs text-slate-500">{insurance ? "A System Admin approves and sends insurance notices." : "An RN or Supervisor releases parent emails."}</p>}
      {error && <p className="field-error" role="alert">{error}</p>}
    </div>
  );
}

function PhoneActions({ l, me, onLine }: { l: QueueLine; me: Me; onLine: (l: QueueLine | null) => void }) {
  const [f, setF] = useState({ number: l.toContact, toName: l.toName, time: nowHHMM(), outcome: "", summary: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = (x: string) => `nq-${x}-${l.notificationKey}`;
  const { problems, check, reset } = useProblems([
    [id("num"), f.number.replace(/\D/g, "").length < 7, "Enter the number dialled."],
    [id("out"), !f.outcome, "Choose the outcome: reached, no answer, or left a message."],
    [id("sum"), f.outcome === "Reached" && !f.summary.trim(), "Write a summary of what was said."],
  ]);
  if (!l.tabs.includes("phone") || !me.capabilities.includes("visit.create")) return null;
  async function log() {
    setError(null);
    if (!check()) return;
    setBusy(true);
    const day = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date(appNow()));
    const r = await api<{ line: QueueLine | null }>("POST", `/api/notifications/${encodeURIComponent(l.notificationKey)}/attempts`, { number: f.number, toName: f.toName, at: f.time ? new Date(easternToUtcMs(day, f.time)).toISOString() : "", outcome: f.outcome, summary: f.summary });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not logged.");
    reset();
    setF({ ...f, outcome: "", summary: "", time: nowHHMM() });
    onLine(r.data.line);
  }
  return (
    <div className="mt-2 rounded-md border border-slate-200 p-2">
      <details className="text-sm">
        <summary className="min-h-11 cursor-pointer py-2 text-program underline">The call script</summary>
        <pre className="whitespace-pre-wrap break-words text-sm">{l.body}</pre>
      </details>
      <div className="grid gap-2 sm:grid-cols-4">
        <div><label htmlFor={id("num")} className="field-label">Number dialled</label><input id={id("num")} type="tel" className="field-input" {...invalid(id("num"), problems)} value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} /><FieldProblem id={id("num")} problems={problems} /></div>
        <div><label htmlFor={id("to")} className="field-label">Spoke to (or tried)</label><input id={id("to")} className="field-input" value={f.toName} onChange={(e) => setF({ ...f, toName: e.target.value })} /></div>
        <div><label htmlFor={id("t")} className="field-label">Time (today)</label><input id={id("t")} type="time" className="field-input" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} /></div>
        <div>
          <label htmlFor={id("out")} className="field-label">Outcome</label>
          <select id={id("out")} className="field-input min-h-11" {...invalid(id("out"), problems)} value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value })}>
            <option value="">Choose…</option>
            <option value="Reached">Reached</option>
            <option value="NoAnswer">No answer</option>
            <option value="LeftMessage">Left a message</option>
          </select>
          <FieldProblem id={id("out")} problems={problems} />
        </div>
      </div>
      {f.outcome === "Reached" && (
        <div className="mt-2"><label htmlFor={id("sum")} className="field-label">Summary of the conversation<span className="text-red-600"> *</span></label><textarea id={id("sum")} className="field-input min-h-16" {...invalid(id("sum"), problems)} value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} /><FieldProblem id={id("sum")} problems={problems} /></div>
      )}
      <button type="button" className="btn-secondary mt-2 min-h-11" disabled={busy} onClick={() => void log()}>{f.outcome === "Reached" ? "Mark reached" : "Log the attempt"}</button>
      {error && <p className="field-error" role="alert">{error}</p>}
    </div>
  );
}

function Line({ l, me, onLine }: { l: QueueLine; me: Me; onLine: (l: QueueLine | null) => void }) {
  return (
    <li className={`rounded-md border p-3 ${l.overdue ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="min-w-0">
          <a className="font-medium text-program underline" href={l.visitKey ? `/visits/${encodeURIComponent(l.visitKey)}` : l.kind === "eow" ? "/eow" : `/chart/${encodeURIComponent(l.personKey)}`}>{l.lastName}, {l.firstName}</a>
          <span className="ml-2 text-sm text-slate-600">{l.city || "Not assigned"}</span>
        </span>
        <span className="flex flex-wrap gap-1 text-xs">
          <span className={`rounded px-1.5 py-0.5 font-semibold ${l.method === "Phone" ? "bg-red-100 text-red-900" : "bg-sky-100 text-sky-900"}`}>{l.method === "Phone" ? "Phone" : "Email"}{l.tier === "Phone" && l.method === "Email" ? " (after the call)" : ""}</span>
          {l.adultSelf && <span className="rounded bg-violet-100 px-1.5 py-0.5 font-semibold text-violet-900">AdultSelf (to the attendee, 18+)</span>}
          {l.kind === "eow" && <span className="rounded bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-900">End-of-week report</span>}
          {l.kind === "packet" && <span className="rounded bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-900">Claim packet attached</span>}
          {l.kind === "insurance" && <span className="rounded bg-indigo-100 px-1.5 py-0.5 font-semibold text-indigo-900">Insurance notice</span>}
          <span className="rounded bg-slate-200 px-1.5 py-0.5">{STATUS_LABEL[l.status] ?? l.status}</span>
          {l.overdue && <span className="rounded bg-amber-300 px-1.5 py-0.5 font-semibold text-amber-950">Overdue</span>}
          {l.edited && <span className="rounded bg-slate-100 px-1.5 py-0.5">Edited</span>}
          {l.templateBadge && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-950">{l.templateBadge}</span>}
        </span>
      </div>
      <p className="mt-1 text-sm text-slate-700">{l.templateLabel} · to {l.toName || "—"}{l.toContact ? ` (${l.toContact})` : ""}</p>
      <p className="text-xs text-slate-500">
        Drafted {stamp(l.createdUtc)}{l.method === "Email" && l.dueByUtc ? ` · due by ${stamp(l.dueByUtc)}` : ""}{l.sentAtUtc ? ` · sent ${stamp(l.sentAtUtc)}${l.approvedByEmail ? ` by ${l.approvedByEmail}` : ""}` : ""}{l.reachedAtUtc ? ` · reached ${stamp(l.reachedAtUtc)}` : ""}
      </p>
      {l.method === "Email" && <p className="mt-1 text-sm"><span className="text-slate-500">Subject:</span> {l.subject}</p>}
      {l.attempts.length > 0 && (
        <ol className="mt-1 list-decimal pl-5 text-sm">
          {l.attempts.map((a, i) => <li key={i}>{stamp(a.atUtc)} · {a.number} · {OUTCOME_LABEL[a.outcome] ?? a.outcome} · {a.byName}{a.summary ? `: ${a.summary}` : ""}</li>)}
        </ol>
      )}
      {l.status === "Sent" && <details className="mt-1 text-sm"><summary className="min-h-11 cursor-pointer py-2 text-program underline">What was sent</summary><pre className="whitespace-pre-wrap break-words">{l.body}</pre></details>}
      {l.method === "Email" ? <EmailActions l={l} me={me} onLine={onLine} /> : <PhoneActions l={l} me={me} onLine={onLine} />}
    </li>
  );
}

export function NotificationQueue({ lines, me, onChanged }: { lines: QueueLine[]; me: Me; onChanged: () => void }): React.ReactElement {
  const [tab, setTab] = useState<(typeof TABS)[number][0]>(() => (new URLSearchParams(window.location.search).get("tab") as never) || "release");
  const [flash, setFlash] = useState<string | null>(null);
  const shown = tab === "all" ? lines : lines.filter((l) => l.tabs.includes(tab as never));
  const count = (t: string) => (t === "all" ? lines.length : lines.filter((l) => l.tabs.includes(t as never)).length);
  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-2 print:hidden" role="tablist">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={`min-h-11 rounded-md border px-3 text-sm ${tab === k ? "border-program bg-program text-white" : "border-slate-300 bg-white"}`} onClick={() => { setTab(k); setFlash(null); }}>{label} ({count(k)})</button>
        ))}
      </div>
      {flash && <p className="mb-2 text-sm font-medium text-emerald-800" role="status">{flash}</p>}
      {shown.length === 0 ? <p className="text-sm text-slate-600">Nothing here.</p> : (
        <ul className="space-y-3">
          {shown.map((l) => <Line key={l.notificationKey} l={l} me={me} onLine={(nl) => { setFlash(nl ? (nl.status === "Sent" ? "Sent" : nl.status === "Reached" ? "Reached: the follow-up email is in To release" : "Saved") : "Saved"); onChanged(); }} />)}
        </ul>
      )}
    </div>
  );
}
